/**
 * Shared `<meeting-slot>` tag matchers. A slot chip is emitted by the AI/editor
 * as `<meeting-slot id="UUID"/>` (self-closing) but may also appear in the
 * open/close pair an HTML parser produces (`<meeting-slot id="UUID">…
 * </meeting-slot>`), so every consumer needs to handle both forms. This module
 * is the single source of truth for the tag PATTERNS; each consumer keeps its
 * own transform (what it does with a match) — see plateEmailSerializer.ts,
 * parse-html-to-plate-value.ts, EmailIframe.tsx, and entitySanitize.ts.
 */

// All three opening-tag patterns below anchor the tag name with `(?=[\s/>])`
// (an exact tag-name boundary) rather than `\b` or `(?![-\w])`. `\b` only
// asserts a word boundary, which EXISTS between `t` and `-`, so `<meeting-slot\b`
// also matches the start of an unrelated custom element like
// `<meeting-slot-data>`. `(?![-\w])` is stricter but still too loose: it only
// forbids `-`/name chars after `meeting-slot`, so a tag like `<meeting-slot.foo>`
// (next char `.`) or `<meeting-slot:x>` (next char `:`) still slips through and
// `[^>]*` swallows the rest — rewriting non-slot markup as a slot token, badge,
// or placeholder. `(?=[\s/>])` instead REQUIRES the character after
// `meeting-slot` to be whitespace, `/`, or `>` — the only characters that can
// legitimately follow a tag name (an attribute-separating space, a self-closing
// `/`, or the tag-closing `>`). So only the exact `meeting-slot` element matches;
// `<meeting-slot-data>`, `<meeting-slots>`, `<meeting-slot.foo>`, and
// `<meeting-slot:x>` all fall through untouched.

// Matches a <meeting-slot> chip in either the canonical void form
// (`<meeting-slot id="X"/>`) or the open/close pair an HTML parser produces
// (`<meeting-slot id="X">…</meeting-slot>`), capturing the id (group 1) and,
// for the paired form, any text folded inside the tag (group 2). Applied
// before the no-id fallback so an id, when present, is always preserved. The
// `\s` before `id=` requires a real attribute boundary so `data-id`/`aria-id`
// can never be captured as the slot id (a `\b` matches the `-id` boundary and
// would). The id capture requires at least one character (`[^"]+`, not `*`):
// an empty `id=""` carries no resolvable reference, so it must fall through to
// the no-id fallback below (which drops the tag) rather than be treated here as
// a reference and leave unresolved `<meeting-slot id=""/>` markup in the body.
export const MEETING_SLOT_TAG_WITH_ID_RE =
  /<meeting-slot(?=[\s/>])[^>]*?\sid="([^"]+)"[^>]*?(?:\/>|>([\s\S]*?)<\/meeting-slot\s*>|>)/gi;

// A <meeting-slot> chip with no id contributes no resolvable reference — drop
// the tag (and any auto-closed pair) so it doesn't leak markup into the body,
// but keep any text folded inside the paired form (group 1) so author content
// next to the chip is not deleted along with the tag.
export const MEETING_SLOT_NO_ID_RE =
  /<meeting-slot(?=[\s/>])[^>]*?(?:\/>|>([\s\S]*?)<\/meeting-slot\s*>|>)/gi;

// Matches ONE opening tag (self-closing or paired-open), capturing its attrs
// (group 1) — `[^>]*` stops at the tag's own `>`, so this never scans past a
// single tag — the match cost is linear. A paired `<open>[\s\S]*?</close>`
// pattern would instead rescan the rest of the document for every unclosed
// opening (quadratic), freezing preview on a hostile draft full of bare
// `<meeting-slot>` openings. Consumers that don't need the attrs (e.g. an
// index scanner) can ignore the capture group.
export const MEETING_SLOT_OPEN_RE = /<meeting-slot(?=[\s/>])([^>]*)>/gi;
// The closing tag is case-insensitive (a paired tag may close with uppercase
// HTML, `</MEETING-SLOT>`) and tolerates whitespace before the `>`
// (`</meeting-slot >`).
export const MEETING_SLOT_CLOSE_RE = /<\/meeting-slot\s*>/i;

// A slot chip's id is always a UUID (the scheduling slot's primary key). Only
// treat a <meeting-slot id="…"> as a slot when its id is UUID-shaped; a
// non-UUID id (e.g. a legacy or malformed reference) falls through to
// ordinary handling rather than producing a slot reference the resolve
// endpoint can't look up.
//
// The end is anchored with `(?![\s\S])` (absolute end of string) rather than
// `$`: in JavaScript `$` also matches immediately BEFORE a final line
// terminator, so `"UUID\n"` would be accepted as a valid id. `(?![\s\S])`
// asserts there is no next character at all, so only the exact UUID matches.
export const SLOT_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?![\s\S])/i;

/** Emits the canonical self-closing `<meeting-slot id="…"/>` reference tag. */
export function emitSlotTag(id: string): string {
  return `<meeting-slot id="${id}"/>`;
}
